// Vendor imports
const classNames = require('classnames')
const React = require('react')
// Shared imports
const BemEntity = require('shared/bem')
// Client V2 common imports
const { CircleButton } = require('client-v2/common/components/buttons')
const Portal = require('client-v2/common/components/portal')
const UserAvatar = require('client-v2/common/components/user-avatar')
const Icon = require('client-v2/common/containers/icons/component')
const { useExperiment } = require('client-v2/common/containers/rollouts')
const { usePropsEventCallback } = require('client-v2/common/hooks/callbacks')
const {
  interestProfilePostsHref
} = require('client-v2/common/models/interest/actions')
const {
  userProfilePostsHref,
  sharedHref
} = require('client-v2/common/models/user/actions')
const tag = require('client-v2/common/utils/hyperscript')
// Sibling imports
const AppSidebarFooter = require('./components/footer')
const AppSidebarSearchModal = require('./components/search-modal')

const bem = BemEntity('AppSidebar')

const moodsAliases = [
  '/mood',
  '/discover'
]

const forYouHrefAliases = [
  '/',
  '/add',
  '/mixit',
  '/top-for-you'
]

const followingHrefAliases = [
  '/recently-added'
]

/**
 * @param {Object} props
 * @param {string} props.className
 * @param {string} props.currentPath
 * @param {boolean} props.isSearchVisible
 * @param {Object} props.user
 * @param {function} props.onAddClick
 * @param {function} props.onAvatarClick
 * @param {function} props.onLinkClick
 * @param {function} props.onSearchClick
 * @param {function} props.onSearchDismiss
 * @returns {React.ElementType}
 */
function AppSidebar(props) {
  const {
    currentPath,
    isSearchVisible,
    user,
    onLinkClick,
    onSearchClick,
    onSearchDismiss
  } = props
  const inboxCount = user.sharesUnreadCount
  // Hooks
  const isMoodsEnabled = useExperiment('*web_moods') !== 'disabled'
  const handleAvatarClick = usePropsEventCallback(props, 'onAvatarClick')
  const AppSidebarLink = useAppSidebarLink({
    currentPath,
    onLinkClick
  })
  // Render
  return (
    tag('nav', {
      'aria-label': 'Main',
      className: classNames(
        props.className,
        bem.block()
      )
    },
      tag('div', {
        className: bem.element('top')
      },
        tag('a', {
          href: '/',
          title: 'Mix Home',
          onClick: onLinkClick
        },
          tag(Icon, {
            className: bem.element('logomark'),
            id: 'mix-logomark'
          })
        ),
        tag('div', {
          className: bem.element('top-spacer')
        }),
        tag(CircleButton, {
          'aria-label': 'Add an URL to your profile',
          className: bem.element('add'),
          onClick: props.onAddClick
        },
          tag(Icon, { id: 'plus' })
        ),
        tag(CircleButton, {
          'aria-label': 'Search',
          className: bem.element('search'),
          onClick: isSearchVisible ? onSearchDismiss : onSearchClick
        },
          tag(Icon, { id: 'search' })
        ),
        tag(UserAvatar, {
          className: bem.element('avatar'),
          badge: false,
          href: true,
          user,
          visible: true,
          onClick: handleAvatarClick
        })
      ),
      isSearchVisible && Portal.toModalRoot(
        tag(AppSidebarSearchModal, {
          className: bem.element('search-modal'),
          onSearchDismiss
        })
      ),
      isMoodsEnabled && (
        tag(AppSidebarLink, {
          href: '/moods',
          hrefAliases: moodsAliases
        },
          'Moods'
        )
      ),
      tag(AppSidebarLink, {
        href: '/for-you',
        hrefAliases: forYouHrefAliases
      },
        'For you'
      ),
      tag(AppSidebarLink, {
        href: '/following',
        hrefAliases: followingHrefAliases
      },
        'Following'
      ),
      !isMoodsEnabled && (
        tag(AppSidebarLink, {
          href: '/popular'
        },
          'Popular'
        )
      ),
      tag(AppSidebarLink, {
        href: userProfilePostsHref(user)
      },
        'My Bookmarks'
      ),
      tag(AppSidebarLink, {
        href: sharedHref()
      },
        'Inbox',
        inboxCount > 0 && (
          tag('span', {
            className: bem.element('inbox-count')
          },
            inboxCount
          )
        )
      ),
      tag('div', {
        className: bem.element('heading', 'interests')
      },
        'My Interests',
        tag('a', {
          'aria-label': 'More interests',
          className: bem.element('more-interests'),
          href: '/settings/interests',
          onClick: onLinkClick
        },
          'More'
        )
      ),
      user.interests.map(interest => (
        tag(AppSidebarLink, {
          key: `interest:${interest.key}`,
          className: bem.element('link', 'interest'),
          href: interestProfilePostsHref(interest)
        },
          interest.name
        )
      )),
      tag('div', {
        className: bem.element('footer-spacer')
      }),
      tag(AppSidebarFooter, {
        className: bem.element('footer')
      })
    )
  )
}

/**
 * @typedef {Object} AppSidebarLinkProps
 * @property {React.ElementType} children
 * @property {string} className
 * @property {string} href
 * @property {string[]} [hrefAliases]
 * @property {function} [onClick]
 */

/**
 * @param {Object} options
 * @param {string} options.currentPath
 * @param {function} options.onLinkClick
 * @returns {React.FunctionComponent<AppSidebarLinkProps>}
 */
function useAppSidebarLink({ currentPath, onLinkClick }) {
  return React.useCallback(
    function AppSidebarLink({
      children,
      className,
      href,
      hrefAliases,
      onClick = onLinkClick
    }) {
      const selected = Boolean(
        href === currentPath ||
        hrefAliases && hrefAliases.includes(currentPath)
      )
      return (
        tag('a', {
          className: classNames(
            className,
            bem.element('link', { selected })
          ),
          href,
          onClick
        },
          children
        )
      )
    },
    [ currentPath, onLinkClick ]
  )
}

module.exports = AppSidebar
